import { Component, DestroyRef, ElementRef, OnInit, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { forkJoin, of } from 'rxjs';
import { catchError, filter, finalize } from 'rxjs/operators';
import { CurrentContextService } from '../../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthSearchOpsTopic,
  liveStatusTranslationKey
} from '../../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../../core/live-updates/live-update.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../../shared/design-system/page.component';
import { AinStateComponent } from '../../../shared/design-system/state.component';
import { StructuredJsonFormComponent } from '../../../shared/design-system/structured-json-form.component';
import {
  SEARCH_OPS_ADS_BRIEF_STATUSES,
  SEARCH_OPS_NEGATIVE_MATCH_TYPES,
  SEARCH_OPS_NEGATIVE_SCOPES,
  SearchAdsBriefDto,
  SearchKeywordClusterListItemDto,
  SearchNegativeKeywordDto,
  SearchPropertyListItemDto,
  SearchSemAccountOptionDto,
  SearchSemBudgetWarningDto,
  SearchSemCampaignLinkOptionDto,
  SearchSemWorkbookDto,
  searchOpsRsaDescriptionFields,
  searchOpsRsaHeadlineFields,
  searchOpsSemNotesFields
} from './growth-search-ops.models';
import { GrowthSearchOpsService } from './growth-search-ops.service';

@Component({
  standalone: true,
  selector: 'ain-growth-search-ops-sem',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    FormsModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    TableModule,
    RouterLink,
    StructuredJsonFormComponent
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.searchops.sem.title')"
      [description]="t('growth.searchops.sem.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.searchops.sem.help.title'"
        [descriptionKey]="'growth.searchops.sem.help.description'"
        [bulletKeys]="[
          'growth.searchops.sem.help.bullets.account',
          'growth.searchops.sem.help.bullets.budget',
          'growth.searchops.sem.help.bullets.upload'
        ]" />

      <div class="toolbar">
        <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load(true)"></button>
        <a pButton severity="secondary" [routerLink]="['/growth/paid-media/accounts']" [label]="t('growth.searchops.sem.actions.accounts')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/paid-media/campaign-links']" [label]="t('growth.searchops.sem.actions.campaignLinks')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/paid-media/roas']" [label]="t('growth.searchops.sem.actions.roas')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/keywords']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.keywords')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/backlog']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.backlog')"></a>
        <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error()" />
      } @else if (properties().length === 0) {
        <ain-state type="empty" [title]="t('growth.searchops.workHome.empty.title')" [message]="t('growth.searchops.workHome.empty.message')" />
      } @else {
        <section class="card">
          <ain-form-field [label]="t('growth.searchops.fields.property')">
            <p-select
              [options]="propertyOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="selectedPropertyId"
              appendTo="body"
              (onChange)="onPropertyChange($event.value)" />
          </ain-form-field>
        </section>

        @if (warnings().length > 0) {
          <section class="card">
            <div class="section-title">
              <h3>{{ t('growth.searchops.sem.sections.budgetWarnings') }}</h3>
              <ain-info-hint
                [titleKey]="'growth.searchops.sem.budgetHelp.title'"
                [descriptionKey]="'growth.searchops.sem.budgetHelp.description'" />
            </div>
            <p-table [value]="warnings()" [paginator]="true" [rows]="10" [tableStyle]="{ 'min-width': '40rem' }">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('growth.searchops.fields.campaign') }}</th>
                  <th>{{ t('growth.searchops.fields.budgetCap') }}</th>
                  <th>{{ t('growth.searchops.fields.requestedSpend') }}</th>
                  <th>{{ t('growth.searchops.fields.warning') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ campaignLabel(row.campaignLinkId, row.externalCampaignId) }}</td>
                  <td>{{ row.budgetCap }}</td>
                  <td>{{ row.requestedSpend ?? '—' }}</td>
                  <td>{{ t('growth.searchops.sem.warnings.overBudget') }}</td>
                </tr>
              </ng-template>
            </p-table>
          </section>
        }

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.sem.sections.workbook') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.sem.workbookHelp.title'"
              [descriptionKey]="'growth.searchops.sem.workbookHelp.description'" />
          </div>
          <ain-form-field [label]="t('growth.searchops.fields.paidMediaAccount')">
            <p-select
              [options]="accountOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="selectedAccountId"
              appendTo="body"
              (onChange)="onAccountChange($event.value)" />
          </ain-form-field>
          <ain-structured-json-form [fields]="notesFields" [(payloadJson)]="notesJson" />
          <button pButton type="button" [disabled]="busy() || !selectedAccountId" [label]="t('common.save')" (click)="saveWorkbook()"></button>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.sem.sections.negatives') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.sem.negativesHelp.title'"
              [descriptionKey]="'growth.searchops.sem.negativesHelp.description'" />
          </div>
          <div class="toolbar">
            <button pButton type="button" [label]="t('growth.searchops.sem.actions.addNegative')" (click)="openNegative()"></button>
            <button pButton type="button" severity="secondary" [disabled]="busy()" [label]="t('growth.searchops.sem.actions.exportCsv')" (click)="exportNegatives()"></button>
            <button pButton type="button" severity="secondary" [label]="t('growth.searchops.sem.actions.importSearchTerms')" (click)="openCsvPicker()"></button>
            <input #csvFileInput type="file" accept=".csv,text/csv" hidden (change)="onCsvSelected($event)" />
          </div>
          @if (importSummary()) {
            <p>{{ importSummary() }}</p>
          }
          <p-table [value]="negatives()" [paginator]="true" [rows]="10" [tableStyle]="{ 'min-width': '40rem' }">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.searchops.fields.phrase') }}</th>
                <th>{{ t('growth.searchops.fields.matchType') }}</th>
                <th>{{ t('growth.searchops.fields.scope') }}</th>
                <th>{{ t('growth.searchops.fields.campaign') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.phrase }}</td>
                <td>{{ t('growth.searchops.negativeMatch.' + row.matchType) }}</td>
                <td>{{ t('growth.searchops.negativeScope.' + row.scope) }}</td>
                <td>{{ campaignLabel(row.campaignLinkId, row.externalCampaignId) }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="4">{{ t('growth.searchops.sem.emptyNegatives') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.sem.sections.briefs') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.sem.briefsHelp.title'"
              [descriptionKey]="'growth.searchops.sem.briefsHelp.description'" />
          </div>
          <button pButton type="button" [label]="t('growth.searchops.sem.actions.addBrief')" (click)="openBrief()"></button>
          <p-table [value]="briefs()" [paginator]="true" [rows]="10" [tableStyle]="{ 'min-width': '40rem' }">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.searchops.fields.cluster') }}</th>
                <th>{{ t('growth.searchops.fields.campaign') }}</th>
                <th>{{ t('growth.searchops.fields.status') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ clusterLabel(row) }}</td>
                <td>{{ campaignLabel(row.campaignLinkId, row.externalCampaignId) }}</td>
                <td>{{ t('growth.searchops.adsBriefStatus.' + row.status) }}</td>
                <td>
                  @if (!row.taskId) {
                    <button pButton type="button" size="small" [disabled]="busy()" [label]="t('growth.searchops.sem.actions.createTask')" (click)="createBriefTask(row)"></button>
                  }
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="4">{{ t('growth.searchops.sem.emptyBriefs') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>

    <p-dialog [(visible)]="negativeOpen" [modal]="true" [header]="t('growth.searchops.sem.dialogs.negative')" [style]="{ width: '32rem' }" appendTo="body">
      <ain-info-hint
        [titleKey]="'growth.searchops.sem.negativeDialogHelp.title'"
        [descriptionKey]="'growth.searchops.sem.negativeDialogHelp.description'" />
      <ain-form-field [label]="t('growth.searchops.fields.phrase')">
        <input pInputText [(ngModel)]="negativeModel.phrase" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.matchType')">
        <p-select [options]="matchOptions()" optionLabel="label" optionValue="value" [(ngModel)]="negativeModel.matchType" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.scope')">
        <p-select [options]="scopeOptions()" optionLabel="label" optionValue="value" [(ngModel)]="negativeModel.scope" appendTo="body" />
      </ain-form-field>
      @if (negativeModel.scope !== 'account') {
        <ain-form-field [label]="t('growth.searchops.fields.campaign')">
          <p-select [options]="campaignOptions()" optionLabel="label" optionValue="value" [(ngModel)]="negativeModel.campaignLinkId" appendTo="body" />
        </ain-form-field>
      }
      <div class="dialog-actions">
        <button pButton type="button" [disabled]="busy()" [label]="t('common.save')" (click)="saveNegative()"></button>
      </div>
    </p-dialog>

    <p-dialog [(visible)]="briefOpen" [modal]="true" [header]="t('growth.searchops.sem.dialogs.brief')" [style]="{ width: '36rem' }" appendTo="body">
      <ain-info-hint
        [titleKey]="'growth.searchops.sem.briefDialogHelp.title'"
        [descriptionKey]="'growth.searchops.sem.briefDialogHelp.description'" />
      <ain-form-field [label]="t('growth.searchops.fields.cluster')">
        <p-select [options]="clusterOptions()" optionLabel="label" optionValue="value" [(ngModel)]="briefModel.clusterId" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.campaign')">
        <p-select [options]="campaignOptions()" optionLabel="label" optionValue="value" [(ngModel)]="briefModel.campaignLinkId" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.status')">
        <p-select [options]="briefStatusOptions()" optionLabel="label" optionValue="value" [(ngModel)]="briefModel.status" appendTo="body" />
      </ain-form-field>
      <ain-structured-json-form [fields]="headlineFields" [(payloadJson)]="briefModel.headlineJson" />
      <ain-structured-json-form [fields]="descriptionFields" [(payloadJson)]="briefModel.descriptionJson" />
      <div class="dialog-actions">
        <button pButton type="button" [disabled]="busy()" [label]="t('common.save')" (click)="saveBrief()"></button>
      </div>
    </p-dialog>
  `,
  styles: [`
    .toolbar, .section-title, .dialog-actions { display: flex; flex-wrap: wrap; gap: 0.75rem; align-items: center; margin-bottom: 1rem; }
    .live-badge { margin-inline-start: auto; }
    .card { margin-bottom: 1.25rem; }
    h3 { margin: 0; }
    td { white-space: normal; overflow-wrap: anywhere; }
  `]
})
export class GrowthSearchOpsSemComponent implements OnInit {
  private readonly api = inject(GrowthSearchOpsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly context = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly destroyRef = inject(DestroyRef);
  readonly csvFileInput = viewChild<ElementRef<HTMLInputElement>>('csvFileInput');
  readonly notesFields = searchOpsSemNotesFields;
  readonly headlineFields = searchOpsRsaHeadlineFields;
  readonly descriptionFields = searchOpsRsaDescriptionFields;
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly loading = signal(true);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly importSummary = signal('');
  readonly properties = signal<SearchPropertyListItemDto[]>([]);
  readonly accounts = signal<SearchSemAccountOptionDto[]>([]);
  readonly campaignLinks = signal<SearchSemCampaignLinkOptionDto[]>([]);
  readonly clusters = signal<SearchKeywordClusterListItemDto[]>([]);
  readonly workbook = signal<SearchSemWorkbookDto | null>(null);
  readonly negatives = signal<SearchNegativeKeywordDto[]>([]);
  readonly briefs = signal<SearchAdsBriefDto[]>([]);
  readonly warnings = signal<SearchSemBudgetWarningDto[]>([]);
  selectedPropertyId: string | null = null;
  selectedAccountId: string | null = null;
  notesJson = '{}';
  negativeOpen = false;
  briefOpen = false;
  negativeModel = { phrase: '', matchType: 'phrase', scope: 'account', campaignLinkId: null as string | null };
  briefModel = {
    clusterId: null as string | null,
    campaignLinkId: null as string | null,
    status: 'draft',
    headlineJson: '{}',
    descriptionJson: '{}'
  };
  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.selectedPropertyId = params.get('propertyId');
      this.load(true);
    });
    this.liveUpdateService.events$
      .pipe(
        filter(event => event.eventType === LiveUpdateEventTypes.growthSearchOpsChanged),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.load(false));
    void this.setupLiveSubscription();
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  propertyOptions() {
    return this.properties().map(item => ({ value: item.id, label: `${item.name} (${item.primaryDomain})` }));
  }

  accountOptions() {
    return this.accounts().map(item => ({
      value: item.id,
      label: `${item.providerKey} (${item.nationCode})`
    }));
  }

  campaignOptions() {
    return this.campaignLinks().map(item => ({
      value: item.id,
      label: `${item.externalCampaignId} · ${item.objective}`
    }));
  }

  clusterOptions() {
    return this.clusters().map(item => ({ value: item.id, label: item.name }));
  }

  matchOptions() {
    return SEARCH_OPS_NEGATIVE_MATCH_TYPES.map(value => ({
      value,
      label: this.t('growth.searchops.negativeMatch.' + value)
    }));
  }

  scopeOptions() {
    return SEARCH_OPS_NEGATIVE_SCOPES.map(value => ({
      value,
      label: this.t('growth.searchops.negativeScope.' + value)
    }));
  }

  briefStatusOptions() {
    return SEARCH_OPS_ADS_BRIEF_STATUSES.map(value => ({
      value,
      label: this.t('growth.searchops.adsBriefStatus.' + value)
    }));
  }

  campaignLabel(campaignLinkId: string | null, externalCampaignId: string | null): string {
    if (!campaignLinkId) {
      return this.t('growth.searchops.sem.accountScope');
    }
    const match = this.campaignLinks().find(item => item.id === campaignLinkId);
    return match?.externalCampaignId || externalCampaignId || this.t('growth.searchops.sem.linkedCampaign');
  }

  clusterLabel(row: SearchAdsBriefDto): string {
    return row.clusterName || this.t('growth.searchops.sem.unmappedCluster');
  }

  propertyQuery() {
    return this.selectedPropertyId ? { propertyId: this.selectedPropertyId } : {};
  }

  onPropertyChange(propertyId: string): void {
    void this.router.navigate([], { relativeTo: this.route, queryParams: { propertyId }, queryParamsHandling: 'merge' });
  }

  onAccountChange(accountId: string): void {
    this.selectedAccountId = accountId;
    this.loadCampaignLinks(accountId);
  }

  openNegative(): void {
    this.negativeModel = { phrase: '', matchType: 'phrase', scope: 'account', campaignLinkId: null };
    this.negativeOpen = true;
  }

  openBrief(): void {
    this.briefModel = {
      clusterId: this.clusters()[0]?.id ?? null,
      campaignLinkId: this.campaignLinks()[0]?.id ?? null,
      status: 'draft',
      headlineJson: '{}',
      descriptionJson: '{}'
    };
    this.briefOpen = true;
  }

  openCsvPicker(): void {
    this.csvFileInput()?.nativeElement.click();
  }

  onCsvSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this.selectedPropertyId) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.busy.set(true);
      this.api.importSearchTerms(this.selectedPropertyId!, reader.result as string).pipe(
        finalize(() => this.busy.set(false))
      ).subscribe({
        next: result => {
          this.importSummary.set(
            this.t('growth.searchops.sem.importResult')
              .replace('{keywords}', `${result.keywordsCreated}`)
              .replace('{negatives}', `${result.negativesCreated}`)
              .replace('{rejected}', `${result.rejected}`)
          );
          this.load(false);
        },
        error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
      });
    };
    reader.readAsText(file);
  }

  saveWorkbook(): void {
    if (!this.selectedPropertyId || !this.selectedAccountId) {
      return;
    }
    this.busy.set(true);
    this.api.upsertSemWorkbook({
      searchPropertyId: this.selectedPropertyId,
      paidMediaAccountId: this.selectedAccountId,
      notes: this.notesJson
    }).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => this.load(false),
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  saveNegative(): void {
    if (!this.selectedPropertyId) {
      return;
    }
    this.busy.set(true);
    this.api.upsertNegative({
      searchPropertyId: this.selectedPropertyId,
      phrase: this.negativeModel.phrase,
      matchType: this.negativeModel.matchType,
      scope: this.negativeModel.scope,
      campaignLinkId: this.negativeModel.scope === 'account' ? null : this.negativeModel.campaignLinkId
    }).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => {
        this.negativeOpen = false;
        this.load(false);
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  saveBrief(): void {
    if (!this.selectedPropertyId) {
      return;
    }
    this.busy.set(true);
    this.api.upsertAdsBrief({
      searchPropertyId: this.selectedPropertyId,
      clusterId: this.briefModel.clusterId,
      campaignLinkId: this.briefModel.campaignLinkId,
      headlineJson: this.briefModel.headlineJson,
      descriptionJson: this.briefModel.descriptionJson,
      status: this.briefModel.status
    }).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => {
        this.briefOpen = false;
        this.load(false);
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  createBriefTask(row: SearchAdsBriefDto): void {
    this.busy.set(true);
    this.api.createTaskFromAdsBrief(row.id).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => this.load(false),
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  exportNegatives(): void {
    if (!this.selectedPropertyId) {
      return;
    }
    this.busy.set(true);
    this.api.exportNegatives(this.selectedPropertyId).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: result => {
        const blob = new Blob([result.csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = result.fileName;
        link.click();
        URL.revokeObjectURL(url);
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  load(showSpinner: boolean): void {
    if (showSpinner) {
      this.loading.set(true);
    }
    this.error.set('');
    forkJoin({
      properties: this.api.listProperties(),
      accounts: this.api.listSemAccounts()
    }).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: payload => {
        this.properties.set(payload.properties);
        this.accounts.set(payload.accounts);
        if (!this.selectedPropertyId && payload.properties[0]) {
          this.selectedPropertyId = payload.properties[0].id;
        }
        this.loadBoards();
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  private loadBoards(): void {
    if (!this.selectedPropertyId) {
      return;
    }
    const propertyId = this.selectedPropertyId;
    forkJoin({
      workbook: this.api.getSemWorkbook(propertyId).pipe(catchError(() => of(null))),
      negatives: this.api.listNegatives(propertyId),
      briefs: this.api.listAdsBriefs(propertyId),
      warnings: this.api.listBudgetWarnings(propertyId),
      clusters: this.api.listClusters(propertyId)
    }).subscribe({
      next: boards => {
        this.workbook.set(boards.workbook);
        this.notesJson = boards.workbook?.notes ?? '{}';
        this.selectedAccountId = boards.workbook?.paidMediaAccountId ?? this.accounts()[0]?.id ?? null;
        this.negatives.set(boards.negatives);
        this.briefs.set(boards.briefs);
        this.warnings.set(boards.warnings);
        this.clusters.set(boards.clusters);
        this.loadCampaignLinks(this.selectedAccountId);
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  private loadCampaignLinks(accountId: string | null): void {
    this.api.listSemCampaignLinks(accountId).subscribe({
      next: items => this.campaignLinks.set(items),
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthSearchOpsTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
